import { Button, useOverlayState } from "@heroui/react";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";

import { DeletePromotionModal } from "@/components/promotions/delete";
import { PromotionSheet } from "@/components/promotions/form";
import { PromotionsTable } from "@/components/promotions/table";
import { useGetAllPromotions } from "@/hooks/api/promotion/useGetAllPromotions";
import { useOpenOnAddParam } from "@/hooks/useOpenOnAddParam";
import { AppPage } from "@/layouts/page";
import { Promotion } from "@/types";

export function PromotionsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const { data, isLoading } = useGetAllPromotions({ page, limit });
  const sheet = useOverlayState();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Promotion | null>(null);

  const openCreate = () => {
    setEditingId(null);
    sheet.open();
  };

  useOpenOnAddParam(openCreate);

  const handleEdit = useCallback(
    (promotion: Promotion) => {
      setEditingId(promotion.id);
      sheet.open();
    },
    [sheet],
  );

  const total = data?.total ?? 0;

  return (
    <AppPage
      action={
        <Button onPress={openCreate}>
          <Plus className="size-4" />
          Add promotion
        </Button>
      }
      subtitle={
        data ? (
          <>
            <span className="tabular-nums">{total}</span>{" "}
            {total === 1 ? "promotion" : "promotions"}
          </>
        ) : (
          "Discounts on products and orders, automatic or with a code"
        )
      }
      title="Promotions"
    >
      <PromotionsTable
        isLoading={isLoading}
        pagination={{
          page,
          pageSize: limit,
          total,
          onPageChange: setPage,
          itemLabel: "promotions",
          onPageSizeChange: (size) => {
            setLimit(size);
            setPage(1);
          },
          pageSizes: [10, 25, 50, 100],
        }}
        promotions={data?.data ?? []}
        onDelete={setDeleting}
        onEdit={handleEdit}
      />
      <PromotionSheet
        isOpen={sheet.isOpen}
        promotionId={editingId}
        onClose={sheet.close}
        onCreated={(created) => setEditingId(created.id)}
      />
      <DeletePromotionModal
        isOpen={deleting !== null}
        promotion={deleting}
        onClose={() => setDeleting(null)}
      />
    </AppPage>
  );
}
